import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, fieldErrors, unwrap, type Schemas } from '../../api/client';
import { useAssetTypes } from '../../api/queries';
import type { AssetType, SpecDefinition } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, Button, Card, CardHeader, Checkbox, Dialog, EmptyState, ErrorBox, Field, FormGrid, Input, Loading, nullIfEmpty, PageHeader, Select, Table, Td, Textarea, Th, Tr, useToast } from '../../components/ui';
import { CATEGORY_LABELS } from '../../lib/format';

const DATA_TYPE_LABELS: Record<string, string> = { text: 'Text', integer: 'Ganzzahl', decimal: 'Dezimalzahl', boolean: 'Ja/Nein', date: 'Datum', enum: 'Auswahlliste' };

export function AssetTypeListPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [showInactive, setShowInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const types = useAssetTypes(showInactive);

  return (
    <>
      <PageHeader
        title="Asset-Typen"
        subtitle="Typen und ihre technischen Felder (z. B. RAM, CPU, Hostname)"
        actions={
          <>
            <Checkbox label="Inaktive anzeigen" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            {can('asset_type:write') && (
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                Typ anlegen
              </Button>
            )}
          </>
        }
      />
      <Card>
        {types.isPending ? (
          <Loading />
        ) : types.error ? (
          <ErrorBox error={types.error} className="m-4" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Bezeichnung</Th>
                <Th>Schlüssel</Th>
                <Th>Kategorie</Th>
                <Th>Art</Th>
                <Th>Technische Felder</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {types.data.map((t) => (
                <Tr key={t.id} onClick={() => navigate(`/asset-types/${t.key}`)}>
                  <Td className="font-medium text-brand-700">{t.label}</Td>
                  <Td className="font-mono text-xs">{t.key}</Td>
                  <Td>{CATEGORY_LABELS[t.category] ?? t.category}</Td>
                  <Td>{t.default_tracking_type === 'component' ? 'Komponente' : 'Gerät'}</Td>
                  <Td className="text-xs text-slate-500">
                    {t.specs
                      .filter((s) => s.is_active)
                      .map((s) => s.label)
                      .join(', ') || '–'}
                  </Td>
                  <Td>{t.is_active ? <Badge tone="green">Aktiv</Badge> : <Badge>Inaktiv</Badge>}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <TypeDialog type={null} open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

export function AssetTypeDetailPage() {
  const { ref = '' } = useParams();
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const [spec, setSpec] = useState<SpecDefinition | 'new' | null>(null);
  const q = useQuery({ queryKey: ['asset-types', 'detail', ref], queryFn: () => unwrap(api.GET('/api/asset-types/{ref}', { params: { path: { ref } } })) });

  if (q.isPending) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const t = q.data;
  const canWrite = can('asset_type:write');
  const specs = [...t.specs].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <>
      <PageHeader
        back={
          <Link to="/asset-types" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
            <ArrowLeft className="size-4" /> Asset-Typen
          </Link>
        }
        title={t.label}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono">{t.key}</span>
            <Badge>{CATEGORY_LABELS[t.category] ?? t.category}</Badge>
            {!t.is_active && <Badge tone="red">Inaktiv</Badge>}
            {t.description}
          </span>
        }
        actions={
          <>
            <Link to={`/assets?type=${t.key}`} className="text-sm text-brand-700 hover:underline">
              Assets dieses Typs
            </Link>
            {canWrite && (
              <Button icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>
                Bearbeiten
              </Button>
            )}
          </>
        }
      />
      <Card>
        <CardHeader
          title="Technische Felder"
          description="Schlüssel und Datentyp sind nach dem Anlegen fest; Felder werden deaktiviert statt gelöscht."
          actions={
            canWrite && (
              <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setSpec('new')}>
                Feld hinzufügen
              </Button>
            )
          }
        />
        {specs.length === 0 ? (
          <EmptyState title="Keine technischen Felder" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Bezeichnung</Th>
                <Th>Schlüssel</Th>
                <Th>Datentyp</Th>
                <Th>Einheit</Th>
                <Th>Werte</Th>
                <Th>Pflicht</Th>
                <Th>Status</Th>
                {canWrite && <Th />}
              </tr>
            </thead>
            <tbody>
              {specs.map((s) => (
                <Tr key={s.id} className={s.is_active ? undefined : 'text-slate-400'}>
                  <Td className="font-medium">{s.label}</Td>
                  <Td className="font-mono text-xs">{s.key}</Td>
                  <Td>{DATA_TYPE_LABELS[s.data_type] ?? s.data_type}</Td>
                  <Td>{s.unit ?? '–'}</Td>
                  <Td className="text-xs">{s.allowed_values?.join(', ') ?? '–'}</Td>
                  <Td>{s.is_required ? 'Ja' : '–'}</Td>
                  <Td>{s.is_active ? <Badge tone="green">Aktiv</Badge> : <Badge>Inaktiv</Badge>}</Td>
                  {canWrite && (
                    <Td className="text-right">
                      <Button size="sm" variant="ghost" aria-label="Bearbeiten" icon={<Pencil className="size-4" />} onClick={() => setSpec(s)} />
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <TypeDialog type={t} open={editing} onClose={() => setEditing(false)} />
      <SpecDialog type={t} spec={spec} onClose={() => setSpec(null)} />
    </>
  );
}

function TypeDialog({ type, open, onClose }: { type: AssetType | null; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [f, setF] = useState({ key: '', label: '', category: 'other', default_tracking_type: 'asset', description: '', sort_order: '100', is_active: true });
  useEffect(() => {
    if (open)
      setF({
        key: type?.key ?? '',
        label: type?.label ?? '',
        category: type?.category ?? 'other',
        default_tracking_type: type?.default_tracking_type ?? 'asset',
        description: type?.description ?? '',
        sort_order: String(type?.sort_order ?? 100),
        is_active: type?.is_active ?? true,
      });
  }, [open, type]);

  const m = useMutation({
    mutationFn: () => {
      const common = {
        label: f.label.trim(),
        category: f.category as Schemas['CreateAssetType']['category'],
        default_tracking_type: f.default_tracking_type as 'asset' | 'component',
        description: nullIfEmpty(f.description),
        sort_order: Number(f.sort_order) || 0,
      };
      return type
        ? unwrap(api.PATCH('/api/asset-types/{ref}', { params: { path: { ref: type.id } }, body: { ...common, is_active: f.is_active } }))
        : unwrap(api.POST('/api/asset-types', { body: { ...common, key: f.key.trim() } }));
    },
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: ['asset-types'] });
      toast(type ? 'Gespeichert' : `Typ ${t.label} angelegt`);
      onClose();
      if (!type) navigate(`/asset-types/${t.key}`);
    },
  });
  const errs = fieldErrors(m.error);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={type ? 'Typ bearbeiten' : 'Typ anlegen'}
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!f.label.trim() || (!type && !f.key.trim())}>
            Speichern
          </Button>
        </>
      }
    >
      <FormGrid>
        <Field label="Schlüssel" required error={errs.key} hint="Kleinbuchstaben, z. B. dockingstation – nicht änderbar">
          {(p) => <Input {...p} value={f.key} disabled={!!type} onChange={(e) => setF({ ...f, key: e.target.value })} />}
        </Field>
        <Field label="Bezeichnung" required error={errs.label}>
          {(p) => <Input {...p} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />}
        </Field>
        <Field label="Kategorie">
          {(p) => (
            <Select {...p} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Standard-Art">
          {(p) => (
            <Select {...p} value={f.default_tracking_type} onChange={(e) => setF({ ...f, default_tracking_type: e.target.value })}>
              <option value="asset">Eigenständiges Gerät</option>
              <option value="component">Komponente</option>
            </Select>
          )}
        </Field>
        <Field label="Sortierung">{(p) => <Input {...p} type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: e.target.value })} />}</Field>
        <Field label="Beschreibung" className="sm:col-span-2">
          {(p) => <Textarea {...p} rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />}
        </Field>
      </FormGrid>
      {type && <Checkbox label="Aktiv (inaktive Typen sind für neue Assets nicht wählbar)" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} />}
      <ErrorBox error={m.error} />
    </Dialog>
  );
}

function SpecDialog({ type, spec, onClose }: { type: AssetType; spec: SpecDefinition | 'new' | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const existing = spec && spec !== 'new' ? spec : null;
  const [f, setF] = useState({ key: '', label: '', data_type: 'text', unit: '', allowed_values: '', is_required: false, description: '', sort_order: '100', is_active: true });
  useEffect(() => {
    if (spec)
      setF({
        key: existing?.key ?? '',
        label: existing?.label ?? '',
        data_type: existing?.data_type ?? 'text',
        unit: existing?.unit ?? '',
        allowed_values: existing?.allowed_values?.join(', ') ?? '',
        is_required: existing?.is_required ?? false,
        description: existing?.description ?? '',
        sort_order: String(existing?.sort_order ?? (type.specs.length + 1) * 10),
        is_active: existing?.is_active ?? true,
      });
  }, [spec]); // eslint-disable-line react-hooks/exhaustive-deps

  const m = useMutation({
    mutationFn: () => {
      const values = f.allowed_values
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
      const common = {
        label: f.label.trim(),
        unit: nullIfEmpty(f.unit),
        is_required: f.is_required,
        description: nullIfEmpty(f.description),
        sort_order: Number(f.sort_order) || 0,
      };
      if (existing) {
        return unwrap(
          api.PATCH('/api/asset-types/{ref}/specs/{key}', {
            params: { path: { ref: type.id, key: existing.key } },
            body: { ...common, is_active: f.is_active, ...(existing.data_type === 'enum' ? { allowed_values: values } : {}) },
          }),
        );
      }
      return unwrap(
        api.POST('/api/asset-types/{ref}/specs', {
          params: { path: { ref: type.id } },
          body: { ...common, key: f.key.trim(), data_type: f.data_type as SpecDefinition['data_type'], allowed_values: f.data_type === 'enum' ? values : null },
        }),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['asset-types'] });
      toast('Feld gespeichert');
      onClose();
    },
  });
  const errs = fieldErrors(m.error);
  const dataType = existing?.data_type ?? f.data_type;

  return (
    <Dialog
      open={!!spec}
      onClose={onClose}
      title={existing ? `Feld „${existing.label}“` : 'Technisches Feld hinzufügen'}
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!f.label.trim() || (!existing && !f.key.trim())}>
            Speichern
          </Button>
        </>
      }
    >
      <FormGrid>
        <Field label="Schlüssel" required error={errs.key} hint="z. B. ram_gb – nicht änderbar">
          {(p) => <Input {...p} value={f.key} disabled={!!existing} onChange={(e) => setF({ ...f, key: e.target.value })} />}
        </Field>
        <Field label="Bezeichnung" required error={errs.label}>
          {(p) => <Input {...p} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />}
        </Field>
        <Field label="Datentyp">
          {(p) => (
            <Select {...p} value={dataType} disabled={!!existing} onChange={(e) => setF({ ...f, data_type: e.target.value })}>
              {Object.entries(DATA_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Einheit" hint="z. B. GB, Zoll">
          {(p) => <Input {...p} value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} />}
        </Field>
        {dataType === 'enum' && (
          <Field label="Erlaubte Werte" required error={errs.allowed_values} hint="Kommagetrennt, z. B. DDR4, DDR5" className="sm:col-span-2">
            {(p) => <Input {...p} value={f.allowed_values} onChange={(e) => setF({ ...f, allowed_values: e.target.value })} />}
          </Field>
        )}
        <Field label="Sortierung">{(p) => <Input {...p} type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: e.target.value })} />}</Field>
        <Field label="Beschreibung / Hilfetext" className="sm:col-span-2">
          {(p) => <Input {...p} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />}
        </Field>
      </FormGrid>
      <div className="flex flex-wrap gap-5">
        <Checkbox label="Pflichtfeld" checked={f.is_required} onChange={(e) => setF({ ...f, is_required: e.target.checked })} />
        {existing && <Checkbox label="Aktiv" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} />}
      </div>
      <ErrorBox error={m.error} />
    </Dialog>
  );
}
